import { SAFETY_MARGIN_OBJECT_NAME } from './SheetCanvas'
import { placedObjectGeometry } from './placedObjectGeometry'

// Serialize a single canvas to a lightweight array.
// canvas.getObjects() returns objects in z-order (index 0 = bottom).
export function getCanvasState(canvas) {
  return canvas.getObjects()
    .filter(obj => obj.name !== SAFETY_MARGIN_OBJECT_NAME)
    .map((obj, i) => {
      const geometry = placedObjectGeometry(obj)
      return {
        objectId:        obj.objectId,
        uploadedImageId: obj.uploadedImageId,
        left:   geometry.left,
        top:    geometry.top,
        scaleX: geometry.scaleX,
        scaleY: geometry.scaleY,
        quantity: 1,
        angle:  geometry.angle,
        flipX:  geometry.flipX || false,
        flipY:  geometry.flipY || false,
        zIndex: i,
      }
    })
}

// Called once all objects are present on the canvas — restores z-order.
function restoreZOrder(canvas, items) {
  const sorted = [...items].sort((a, b) => a.zIndex - b.zIndex)
  sorted.forEach(item => {
    const obj = canvas.getObjects().find(o => o.objectId === item.objectId)
    if (obj) canvas.moveObjectTo(obj, item.zIndex)
  })
  canvas.requestRenderAll()
}

// Restore canvas to match a lightweight snapshot.
// Does NOT call loadFromJSON (avoids URL re-fetch).
// onComplete fires after ALL async clone() callbacks resolve and z-order is restored.
export function applyCanvasState(canvas, items, imagePool, onComplete, shouldApply = () => true) {
  // Snapshots use sheet coordinates. Dissolve any temporary selection group
  // before applying them, or its transform will be applied a second time.
  canvas.discardActiveObject?.()
  const objects = canvas.getObjects().filter(obj => obj.name !== SAFETY_MARGIN_OBJECT_NAME)

  // Phase 1: Apply positions to objects already on canvas
  items.forEach(item => {
    const obj = objects.find(o => o.objectId === item.objectId)
    if (!obj) return
    obj.set({ left: item.left, top: item.top, scaleX: item.scaleX, scaleY: item.scaleY, quantity: 1, angle: item.angle, flipX: item.flipX, flipY: item.flipY })
    obj.setCoords()
  })

  // Phase 2: Remove objects present on canvas but absent from snapshot (undoing an add)
  const snapshotIds = new Set(items.map(i => i.objectId))
  objects.forEach(obj => {
    if (!snapshotIds.has(obj.objectId)) canvas.remove(obj)
  })

  // Phase 3: Re-add objects present in snapshot but absent from canvas (undoing a delete).
  // clone() is async — z-order restore and onComplete are deferred until ALL clones land.
  // canvas.getObjects() is called here (after Phase 2) because canvas.remove() is synchronous
  // and the post-removal state must be used to compute which objects are missing.
  const canvasIds = new Set(canvas.getObjects().map(o => o.objectId))
  const toAdd = items.filter(item => !canvasIds.has(item.objectId))

  const finish = () => {
    if (!shouldApply()) return
    restoreZOrder(canvas, items)
    onComplete?.()
  }

  if (toAdd.length === 0) { finish(); return }

  let pending = toAdd.length
  toAdd.forEach(item => {
    const source = imagePool.get(String(item.uploadedImageId))
    if (!source) {
      console.error(`[undo] imagePool missing source for uploadedImageId=${item.uploadedImageId}`)
      pending--; if (pending === 0) finish(); return
    }
    let clonePromise
    try {
      clonePromise = source.clone()
    } catch {
      pending--
      if (pending === 0) finish()
      return
    }
    Promise.resolve(clonePromise).then(clone => {
      if (!shouldApply()) return
      clone.set({
        left:            item.left,
        top:             item.top,
        scaleX:          item.scaleX,
        scaleY:          item.scaleY,
        quantity:        1,
        angle:           item.angle,
        flipX:           item.flipX,
        flipY:           item.flipY,
        uploadedImageId: item.uploadedImageId,
        objectId:        item.objectId,  // restore exact objectId so future snapshots match
      })
      clone.setCoords()
      canvas.add(clone)
      pending--
      if (pending === 0) finish()
    }).catch(() => {
      pending--
      if (pending === 0) finish()
    })
  })
}
